import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAlert } from '../Components/AlertContext';

import Breadcrumbs from '../Components/Breadcrumbs';
import PageLoader from '../Components/PageLoader';
import PostCallAnalyticsExportBox from '../Components/PostCallAnalyticsExportBox';
import PostCallAnalyticsSummary from '../Components/PostCallAnalyticsSummary';

import {
  PostCallAnalysisApiProvider,
  usePostCallAnalysisApi,
} from '../Services/PostCallAnalysisApiProvider';
import {
  PostCallAnalysisChatItem,
  PostCallAnalysisMetric,
  PostCallAnalysisSummaryApi,
} from '../Services/PostCallAnalysisApiProviderTypes';
import PostCallAnalyticsFilters, {
  PostCallAnalyticsFilterSelection,
} from '../Components/PostCallAnalyticsFilters';

import './PostCallAnalysisDetail.css';

function PostCallAnalyticsContent() {
  const { configId } = useParams();
  const { showAlert } = useAlert();

  const { getConfigById, getAnalyticsSummary, getAnalyticsChats, exportAnalytics } =
    usePostCallAnalysisApi();

  const [loading, setLoading] = useState<boolean>(true);
  const [metrics, setMetrics] = useState<PostCallAnalysisMetric[]>([]);

  const [summaryLoading, setSummaryLoading] = useState<boolean>(false);
  const [summary, setSummary] = useState<PostCallAnalysisSummaryApi | null>(null);

  const [chats, setChats] = useState<(PostCallAnalysisChatItem & { id: string })[]>([]);
  const [chatsTotal, setChatsTotal] = useState<number>(0);
  const [chatsLoading, setChatsLoading] = useState<boolean>(false);
  const [chatsSkip, setChatsSkip] = useState<number>(0);
  const [chatsLimit, setChatsLimit] = useState<number>(10);

  const [startDate, setStartDate] = useState<string | undefined>();
  const [endDate, setEndDate] = useState<string | undefined>();

  const [selectedFilters, setSelectedFilters] = useState<PostCallAnalyticsFilterSelection>({});
  const [filterOptions, setFilterOptions] = useState<PostCallAnalysisSummaryApi | null>(null);

  const filtersParam = useMemo(() => {
    const entries = Object.entries(selectedFilters)
      .filter(([, filter]) => !Array.isArray(filter) || filter.length > 0)
      .map(([metric_key, filter]) =>
        Array.isArray(filter) ? { metric_key, values: filter } : { metric_key, ...filter },
      );
    return entries.length > 0 ? JSON.stringify(entries) : undefined;
  }, [selectedFilters]);

  const numericMetrics = useMemo(
    () => (metrics || []).filter((m) => m.metric_type === 'numeric'),
    [metrics],
  );

  const categoricalMetrics = useMemo(
    () => (metrics || []).filter((m) => m.metric_type === 'categorical'),
    [metrics],
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const config = await getConfigById(configId, true);
      setMetrics(config.metrics || []);
    } catch (err) {
      console.error(err);
      showAlert('fail', 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  }, [configId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchSummary = useCallback(async () => {
    if (!startDate) return;

    if (numericMetrics.length === 0 && categoricalMetrics.length === 0) {
      setSummary(null);
      return;
    }

    setSummaryLoading(true);
    try {
      const data = await getAnalyticsSummary(configId, {
        start_date: startDate,
        end_date: endDate,
        filters: filtersParam,
      });
      setSummary(data);
      if (!filtersParam) {
        setFilterOptions(data);
      }
    } catch (err) {
      console.error(err);
      showAlert('fail', 'Failed to load post-call analytics summary');
    } finally {
      setSummaryLoading(false);
    }
  }, [
    configId,
    startDate,
    endDate,
    filtersParam,
    numericMetrics,
    categoricalMetrics,
    getAnalyticsSummary,
    showAlert,
  ]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const fetchChats = useCallback(async () => {
    // The backend substitutes the current date when end_date is missing.
    if (!startDate) return;

    setChatsLoading(true);
    try {
      const data = await getAnalyticsChats(configId, {
        start_date: startDate,
        end_date: endDate,
        skip: chatsSkip,
        limit: chatsLimit,
        filters: filtersParam,
      });
      setChats((data.items || []).map((c) => ({ ...c, id: c.thread_key })));
      setChatsTotal(data.total_count || 0);
    } catch (err) {
      console.error(err);
      showAlert('fail', 'Failed to load chats');
    } finally {
      setChatsLoading(false);
    }
  }, [
    configId,
    startDate,
    endDate,
    chatsSkip,
    chatsLimit,
    filtersParam,
    getAnalyticsChats,
    showAlert,
  ]);

  useEffect(() => {
    fetchChats();
  }, [fetchChats]);

  const handlePeriodChange = useCallback(
    (period: { start_date?: string; end_date?: string } | null) => {
      setStartDate(period?.start_date);
      setEndDate(period?.end_date);
      setChatsSkip(0);
    },
    [],
  );

  const handleChatsPageChange = useCallback((skip: number, limit: number) => {
    setChatsSkip(skip);
    setChatsLimit(limit);
  }, []);

  const handleFiltersChange = useCallback((next: PostCallAnalyticsFilterSelection) => {
    setSelectedFilters(next);
    setChatsSkip(0);
  }, []);

  const handleRefresh = useCallback(() => {
    fetchSummary();
    fetchChats();
  }, [fetchSummary, fetchChats]);

  const handleExport = useCallback(
    async (params: Parameters<typeof exportAnalytics>[1]) => {
      try {
        await exportAnalytics(configId, params);
      } catch (err) {
        console.error(err);
        showAlert('fail', 'Failed to export post-call analytics');
      }
    },
    [configId, exportAnalytics, showAlert],
  );

  if (loading) return <PageLoader />;

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row mt-2">
        <PostCallAnalyticsSummary
          summary={summary}
          loading={summaryLoading}
          numericMetrics={numericMetrics}
          categoricalMetrics={categoricalMetrics}
          chats={chats}
          chatsTotal={chatsTotal}
          chatsLoading={chatsLoading}
          chatsLimit={chatsLimit}
          onChatsPageChange={handleChatsPageChange}
          onPeriodChange={handlePeriodChange}
          onRefresh={handleRefresh}
          filtersSlot={
            <PostCallAnalyticsFilters
              metrics={metrics}
              optionSource={filterOptions}
              selected={selectedFilters}
              onChange={handleFiltersChange}
            />
          }
          exportSlot={
            <PostCallAnalyticsExportBox
              metrics={metrics}
              startDate={startDate}
              endDate={endDate}
              filters={filtersParam}
              onExport={handleExport}
            />
          }
        />
      </div>
    </div>
  );
}

function PostCallAnalytics() {
  return (
    <PostCallAnalysisApiProvider>
      <PostCallAnalyticsContent />
    </PostCallAnalysisApiProvider>
  );
}

export default PostCallAnalytics;
